import React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { useOpportunityManagement } from "@/opportunity/OpportunityManagementContext";
import useOpportunityStatuses from "@/opportunity/useOpportunityStatuses";
import { useOpportunityCategories } from "@/api/api";
import { useLocations } from "@/location/api";
import { useOrganizationsMinimal } from "@/organization/api";


const Filters = ({ organizationId } : { organizationId?: number }) => {

    const { filters, setFilters } = useOpportunityManagement();
    const { data: statusOptions } = useOpportunityStatuses();

    const { data: categoriesData } = useOpportunityCategories({});
    const categories = categoriesData.results || []

    const { data: { results: organizations } } = useOrganizationsMinimal();

    // Use useLocations with organization filtering
    const { data: locationsData } = useLocations({
        pageSize: 100,
        filters: {
            organization_id: filters.organization_id || (organizationId ? [organizationId] : undefined)
        }
    });
    const locations = locationsData?.results || [];

    const locationOptions = [...new Set(locations.map(loc => loc.name))].sort();

    const onStatusToggle = (status: string) => {
        setFilters((prevFilters) => {
            const newStatus = prevFilters.status.includes(status)
              ? prevFilters.status.filter((s) => s !== status)
              : [...prevFilters.status, status];
            return { ...prevFilters, status: newStatus };
        });
    };

    const onOrganizationSelect = (organizationId: number | undefined) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            organization_id: organizationId,
            location: "", // Reset location when organization changes
        }));
    };

    const onLocationSelect = (location: string) => {
        setFilters((prevFilters) => ({ ...prevFilters, location }));
    };

    const onCategorySelect = (category: string) => {
        setFilters((prevFilters) => ({ ...prevFilters, category }));
    };

    return (
        <div className="flex flex-wrap gap-2">
            {/* Organization Filter */}
            {
                !organizationId && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className="whitespace-nowrap flex items-center justify-between px-4 py-2 text-sm text-left text-gray-700 bg-white rounded-md border border-gray-300 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                                {filters.organization_id
                                    ? organizations.find((org) => org.id === filters.organization_id)?.name
                                    : "All organizations"}
                                <ChevronDown className="ml-2 w-4 h-4" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="max-h-60 overflow-y-auto">
                            <DropdownMenuItem onSelect={() => onOrganizationSelect(undefined)}>
                                All organizations
                            </DropdownMenuItem>
                            {organizations.map((organization) => (
                                <DropdownMenuItem
                                    key={organization.id}
                                    onSelect={() => onOrganizationSelect(organization.id)}
                                >
                                    {organization.name}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>

              )
            }

            {/* Location Filter */}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button className="whitespace-nowrap flex items-center justify-between px-4 py-2 text-sm text-left text-gray-700 bg-white rounded-md border border-gray-300 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                        {filters.location || "All locations"}
                        <ChevronDown className="ml-2 w-4 h-4" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="max-h-60 overflow-y-auto">
                    <DropdownMenuItem onSelect={() => onLocationSelect("")}>
                        All locations
                    </DropdownMenuItem>
                    {locationOptions.map((location) => (
                        <DropdownMenuItem
                            key={location}
                            onSelect={() => onLocationSelect(location)}
                        >
                            {location}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Status Filter */}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button className="flex items-center justify-between px-4 py-2 text-sm text-left text-gray-700 bg-white rounded-md border border-gray-300 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                        {
                            filters.status.length == 0 || filters.status.length === statusOptions.length
                                ? "All statuses"
                                : `${filters.status.length} Status${filters.status.length !== 1 ? 'es' : ''} selected`
                        }
                        <ChevronDown className="ml-2 w-4 h-4" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="max-h-60 overflow-y-auto">
                    <DropdownMenuItem onSelect={() => {
                        setFilters((prevFilters) => ({
                            ...prevFilters,
                            status: statusOptions.map((status) => status.name)
                      }))
                    }}>
                        Select all
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => {
                        setFilters((prevFilters) => ({
                            ...prevFilters,
                            status: []
                      }))
                    }}>
                        Select none
                    </DropdownMenuItem>
                    {statusOptions.map((status) => (
                        <DropdownMenuItem
                            key={status.name}
                            onSelect={() => onStatusToggle(status.name)}
                        >
                            <input
                                type="checkbox"
                                checked={filters.status.includes(status.name)}
                                readOnly
                                className="mr-2"
                            />
                            {status.name}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Category Filter */}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button className="whitespace-nowrap flex items-center justify-between px-4 py-2 text-sm text-left text-gray-700 bg-white rounded-md border border-gray-300 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                        {filters.category || "All categories"}
                        <ChevronDown className="ml-2 w-4 h-4" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="max-h-60 overflow-y-auto">
                    <DropdownMenuItem onSelect={() => onCategorySelect("")}>
                        All categories
                    </DropdownMenuItem>
                    {categories.map((category) => (
                        <DropdownMenuItem
                            key={category.name}
                            onSelect={() => onCategorySelect(category.name)}
                        >
                            {category.name}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
};

export default Filters;